import { ticketTypeEnum } from '../constants/enums';
import { BET_SLIP_TABS } from '../constants/defaultValues';

class BetslipCalculator {
    calculateTicketDetails = ({ activeTab, betLines, stakes, selectedCombinations = [] }) => {
        let ticketDetails = {};

        if (activeTab === BET_SLIP_TABS.single.id) {
            ticketDetails.totalOdds = getTotalOddsOfSingle(betLines);
            ticketDetails.totalStake = getTotalStake(betLines);
            ticketDetails.toWin = calculateWinings(betLines);
            const winningTax = this.getWinningTax(ticketDetails.toWin, ticketDetails.totalStake);
            ticketDetails.potentialPayout = this.getPotentialPayout(
                ticketDetails.toWin,
                winningTax,
            );
            ticketDetails.expectedBetsCount = betLines.filter(bet => !bet.closed).length;
        } else if (activeTab === BET_SLIP_TABS.combo.id) {
            ticketDetails.totalOdds = getTotalOddsOfCombo(betLines);
            ticketDetails.totalStake = getTotalStake(
                betLines,
                BET_SLIP_TABS.combo.id,
                stakes[BET_SLIP_TABS.combo.id],
            );
            ticketDetails.toWin = calculateWinings(
                betLines,
                BET_SLIP_TABS.combo.id,
                stakes[BET_SLIP_TABS.combo.id],
                ticketDetails.totalOdds,
            );
            const winningTax = this.getWinningTax(ticketDetails.toWin, ticketDetails.totalStake);
            ticketDetails.potentialPayout = this.getPotentialPayout(
                ticketDetails.toWin,
                winningTax,
            );
            ticketDetails.expectedBetsCount = 1;
        } else if (activeTab === BET_SLIP_TABS.system.id) {
            if (selectedCombinations.length > 0) {
                const systems = this.generateSystems(betLines, stakes[BET_SLIP_TABS.system.id]);
                ticketDetails.totalOdds = 0;
                ticketDetails.totalStake = this.calculateSystemTotalStake({
                    systems,
                    stakeAmount: stakes[BET_SLIP_TABS.system.id],
                    selectedCombinations,
                });
                ticketDetails.allSystemCombinationOdds = this.getAllSystemCombinationOdds({
                    bets: betLines,
                    systems,
                    stake: ticketDetails.totalStake,
                    selectedCombinations,
                });
                ticketDetails.toWin = this.calculateSystemWinnings({
                    allCombinationOdds: ticketDetails.allSystemCombinationOdds,
                    systems,
                    stake: ticketDetails.totalStake,
                    selectedCombinations,
                });
                const winningTax = this.getWinningTax(
                    ticketDetails.toWin,
                    ticketDetails.totalStake,
                );
                ticketDetails.potentialPayout = this.getPotentialPayout(
                    ticketDetails.toWin,
                    winningTax,
                );
                ticketDetails.expectedBetsCount = this.getExpectedSystemBets(
                    systems,
                    selectedCombinations,
                );
            } else {
                ticketDetails.totalOdds = 0;
                ticketDetails.totalStake = 0;
                ticketDetails.allSystemCombinationOdds = [];
                ticketDetails.toWin = 0;
                ticketDetails.potentialPayout = 0;
                ticketDetails.expectedBetsCount = 0;
            }
        }

        return ticketDetails;
    };

    getPotentialPayout(toWin, winningTax) {
        return toWin <= 500 ? toWin : toWin - winningTax;
    }

    getWinningTax(toWin, totalStake) {
        const taxFreeAmount = 500;
        const maxWinPercent = 0.1;
        const winTax = (toWin - (totalStake + taxFreeAmount)) * maxWinPercent;

        return winTax < 0 ? 0 : winTax;
    }

    getBankersSum(bets) {
        let sum = 1;
        this.getBankers(bets).forEach(bet => (sum *= bet.odd));
        return sum;
    }

    getSelectedSystems(systems, selectedCombinations) {
        return systems.filter(system => selectedCombinations.includes(system.id));
    }

    getExpectedSystemBets(systems, selectedCombinations) {
        let expectedBets = 0;

        this.getSelectedSystems(systems, selectedCombinations).forEach(
            item => (expectedBets += item.combinations),
        );

        return expectedBets;
    }

    calculateSystemTotalStake = ({ systems, stakeAmount, selectedCombinations }) => {
        const bets = this.getExpectedSystemBets(systems, selectedCombinations);

        return bets * stakeAmount;
    };

    getStakePerSystem({ systems, stake, selectedCombinations }) {
        const selectedSystems = this.getSelectedSystems(systems, selectedCombinations);

        let combinations = 0;

        selectedSystems.forEach(item => (combinations += item.combinations));

        return stake / combinations;
    }

    getAllSystemCombinationOdds({ bets, systems, stake, selectedCombinations }) {
        const allOdds = [];

        const nonBankers = this.getNonBankers(bets);
        const bankers = this.getBankers(bets);

        let bankersSum = 1;

        bankers.forEach(item => (bankersSum *= item.odd));

        let combsOdds = [];

        nonBankers.forEach(item => {
            combsOdds.push(item.odd);
        });

        const selectedSystems = this.getSelectedSystems(systems, selectedCombinations);

        for (let i = 0; i < selectedSystems.length; i++) {
            let systemOddsSum = [];
            selectedSystems[i].maxWinning = 0;

            getSystemCombsOdds(combsOdds, selectedSystems[i].id, arrayComb => {
                let sum = 1;

                for (let j = 0; j < arrayComb.length; j++) {
                    sum *= arrayComb[j];
                }

                systemOddsSum.push(sum);
            });

            for (let k = 0; k < systemOddsSum.length; k++) {
                allOdds.push(systemOddsSum[k] * bankersSum);
            }
        }

        return allOdds;
    }

    calculateSystemWinnings({ allCombinationOdds, systems, stake, selectedCombinations }) {
        let maxWinning = 0;

        const stakePerSystem = this.getStakePerSystem({ systems, stake, selectedCombinations });

        for (let i = 0; i < allCombinationOdds.length; i++) {
            maxWinning += allCombinationOdds[i] * stakePerSystem;
        }

        return Math.min(maxWinning, 250000);
    }

    getBankers(bets) {
        return bets.filter(bet => bet.banco);
    }

    getNonBankers(bets) {
        return bets.filter(bet => !bet.banco);
    }

    getNumberOfCombs(num1, num2) {
        const { lower, higher } = {
            lower: num1 > num2 ? num2 : num1,
            higher: num1 > num2 ? num1 : num2,
        };

        let x = 1;
        let y = 1;

        for (let i = higher; i > higher - lower; i -= 1) {
            x *= i;
        }

        for (let i = lower; i > 0; i -= 1) {
            y *= i;
        }

        return parseFloat(x) / parseFloat(y);
    }

    generateSystems = (bets, stake) => {
        let systems = [];
        const nonBankers = this.getNonBankers(bets);
        const hasInvalidCombinations = this.validateSystemMbn({
            betLines: nonBankers,
        });

        for (let i = 1; i < nonBankers.length - 1; i++) {
            if (hasInvalidCombinations && i === 1) continue;

            const system = {
                key: `${i + 1}-${new Date().getTime()}`,
                id: i + 1,
                of: nonBankers.length,
                active: false,
                stake,
                combinations: getNumberOfCombs(i + 1, nonBankers.length),
            };
            system.stakePerCombination = stake / system.combinations;
            systems.push(system);
        }

        return systems;
    };

    validateComboMbn = ({ mbns }) => {
        const hasOneMbn = mbns.some(mbn => mbn === 1);

        let hasErrors = false;

        if (!hasOneMbn) {
            const maxMbn = Math.max(...mbns);

            if (mbns.length < maxMbn) {
                hasErrors = true;
            }
        }

        return hasErrors;
    };

    validateSystemMbn = ({ betLines }) => {
        const mbns = betLines.map(b => b.mbn);

        const combinations = [];

        for (let i = 0; i < mbns.length; i++) {
            for (let j = i + 1; j < mbns.length; j++) {
                combinations.push([mbns[i], mbns[j]]);
            }
        }

        const hasInvalidCombinations = combinations.some(combination => {
            const hasError = this.validateComboMbn({
                mbns: combination,
            });

            return hasError;
        });

        return hasInvalidCombinations;
    };
}

const getSystemCombsOdds = (systemOdds, systemLowerNumber, callback) => {
    let { length } = systemOdds;
    let combinations = [];
    const inner = (start, choose_) => {
        if (choose_ === 0) {
            callback(combinations);
        } else {
            for (let i = start; i <= length - choose_; ++i) {
                combinations.push(systemOdds[i]);
                inner(i + 1, choose_ - 1);
                combinations.pop();
            }
        }
    };
    inner(0, systemLowerNumber);
};

const getNumberOfBets = bets => {
    const filteredBets = bets.filter(bet => !bet.closed);
    return filteredBets.length;
};

const getTotalOddsOfSingle = bets => {
    if (bets.length === 1 && !bets.some(bet => bet.closed)) {
        let total = null;
        total = bets[0].odd;
        return total;
    }

    return null;
};

const getTotalOddsOfCombo = bets => {
    if (bets.length > 0) {
        let total = 1;
        bets.forEach(bet => {
            if (!bet.closed) {
                total *= parseFloat(bet.odd);
            }
        });
        return total;
    }
    return null;
};

const getTotalStake = (bets, type = ticketTypeEnum.SINGLE, stakeValue = 0.5) => {
    let totalStake = 0;

    if (type === ticketTypeEnum.SINGLE) {
        bets.forEach(bet => {
            if (!bet.closed) {
                totalStake += parseFloat(bet.stake) || 0;
            }
        });
    } else if (type === ticketTypeEnum.COMBO) {
        totalStake = stakeValue;
    } else if (type === ticketTypeEnum.SYSTEM) {
        totalStake = stakeValue;
    }

    return Number(totalStake);
};

const calculateWinings = (bets, type = ticketTypeEnum.SINGLE, stakeValue = 0.5, totalOdds) => {
    let winnings = 0;

    if (type === ticketTypeEnum.SINGLE) {
        bets.forEach(bet => {
            if (!bet.closed) {
                winnings += parseFloat(bet.odd) * bet.stake;
            }
        });
    } else if (type === ticketTypeEnum.COMBO) {
        winnings = totalOdds * stakeValue;
    } else if (type === ticketTypeEnum.SYSTEM) {
        winnings = totalOdds * stakeValue;
    }

    return Math.min(winnings, 250000);
};

const getNonBankers = bets => {
    return bets.filter(bet => !bet.banco);
};

const getNumberOfCombs = (num1, num2) => {
    const { lower, higher } = {
        lower: num1 > num2 ? num2 : num1,
        higher: num1 > num2 ? num1 : num2,
    };

    let x = 1;
    let y = 1;

    for (let i = higher; i > higher - lower; i -= 1) {
        x *= i;
    }

    for (let i = lower; i > 0; i -= 1) {
        y *= i;
    }

    return parseFloat(x) / parseFloat(y);
};

export {
    getNumberOfBets,
    getTotalOddsOfSingle,
    getTotalOddsOfCombo,
    getTotalStake,
    calculateWinings,
    getNonBankers,
};

export default new BetslipCalculator();
